import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { API_URL } from "../../apiConfig";

const T = {
  ink:      "#1a1a1c",
  ink2:     "#4a4a50",
  ink3:     "#86868b",
  ink4:     "#b4b3b0",
  hair:     "rgba(20,20,22,0.07)",
  surface2: "#f9f8f6",
  accent:   "#1e3a8a",
  danger:   "#C5221F",
  font:     "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
};

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";
const authorName = (email) => (email || "").split("@")[0] || "—";

/**
 * Comentários internos de uma ativação (lista + campo para comentar).
 * Usado na Lista de Ativações (modal de comentários e de status) e nos Detalhes.
 * `onUpdated` recebe a ativação com os comentários atualizados.
 */
export default function ActivationComments({ activation, onUpdated, maxHeight = 260, autoFocus = false }) {
  const [comments, setComments] = useState(activation?.comments || []);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    setComments(activation?.comments || []);
    setText("");
    setErr("");
  }, [activation?._id, activation?.comments]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments.length]);

  async function submit() {
    if (!activation?._id || !text.trim() || saving) return;
    setSaving(true);
    setErr("");
    try {
      const resp = await fetch(`${API_URL}/admin/activations/${activation._id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: JSON.stringify({ text: text.trim() }),
      });
      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload?.message || "Falha ao salvar comentário");
      const next = payload.activation?.comments || [];
      setComments(next);
      setText("");
      onUpdated?.({ ...activation, comments: next });
    } catch (e) {
      setErr(e?.message || "Falha ao salvar comentário");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: T.font }}>
      <div ref={listRef} style={{ maxHeight, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {comments.length === 0 && (
          <div style={{ fontSize: 12.5, color: T.ink4, textAlign: "center", padding: "10px 0" }}>Nenhum comentário ainda.</div>
        )}
        {comments.map((c) => (
          <div key={c._id || c.createdAt}>
            <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 3 }}>
              <strong style={{ color: T.ink2, fontWeight: 600 }}>{authorName(c.authorEmail)}</strong> · {fmtDateTime(c.createdAt)}
            </div>
            <div style={{ fontSize: 13, color: T.ink, whiteSpace: "pre-wrap", wordBreak: "break-word", background: T.surface2, borderRadius: "4px 12px 12px 12px", padding: "8px 12px" }}>
              {c.text}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          rows={2}
          autoFocus={autoFocus}
          maxLength={2000}
          placeholder="Escreva um comentário interno…  (Ctrl/⌘ + Enter envia)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          style={{ flex: 1, resize: "vertical", borderRadius: 10, border: `1px solid ${T.hair}`, background: "#fff", fontSize: 13, padding: "8px 10px", fontFamily: T.font, color: T.ink, outline: "none" }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || saving}
          title="Comentar"
          style={{ height: 38, padding: "0 14px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, fontFamily: T.font, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", opacity: !text.trim() || saving ? 0.5 : 1, flexShrink: 0 }}
        >
          {saving ? <Spinner size="sm" animation="border" /> : <><PaperAirplaneIcon style={{ width: 14, height: 14 }} /> Comentar</>}
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: T.danger }}>{err}</div>}
    </div>
  );
}
